import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useSupabaseDocsGrouped, useSupabaseDocsSearch } from '@/hooks/useSupabaseDocs';
import { faqs, FAQ } from '@/data/support/faqs';
import FAQSection from '@/components/support/FAQSection';
import ContactSection from '@/components/support/ContactSection';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import SEO from '@/components/SEO';

const TOPIC_META: Record<string, { title: string; order: number }> = {
  getting_started: { title: 'Get Started Your First Week', order: 1 },
  dashboard: { title: 'Understand Your Dashboard', order: 2 },
  room_scans: { title: 'Scan a Room for Safety', order: 3 },
  wellness: { title: 'Track Your Daily Wellness', order: 4 },
  family_connection: { title: 'Connect with Family', order: 5 },
  privacy_account: { title: 'Manage Your Account and Privacy', order: 6 },
  fitness_videos: { title: 'Use Fitness Videos', order: 7 },
  brain_games: { title: 'Play Brain Games', order: 8 },
  log_activity: { title: 'Log Your Activities', order: 9 },
};

const TOOL_CARDS = [
  {
    name: 'Home Safety Checks',
    description: "Turn your phone camera into a safety partner. Scan a room to uncover hidden risks and receive personalized safety recommendations, without appointments and without anyone entering your home.",
    link: '/support/older-adults/understanding-thrivevision',
  },
  {
    name: 'MyDaily Check-In',
    description: "Your daily signal. Reflect, choose how you're feeling, and strengthen your ThriveScore.",
    link: '/support/older-adults/dashboard-daily-checkin',
  },
  {
    name: 'ThriveScore',
    description: "Your independence score. The blue ring tracks home safety. The orange shimmer tracks wellness. Together, they show how safely and actively you're living.",
    link: '/support/older-adults/dashboard-thrivescore-and-focus',
  },
  {
    name: 'ThriveCircle',
    description: 'Your private support circle. Family first, but friends and caregivers can join when you choose.',
    link: '/support/older-adults/what-thrivecircle-is',
  },
  {
    name: 'AI Companions',
    description: 'Your on-demand support team. From home safety to health to pet care, get clear answers anytime.',
    link: '/support/older-adults/understanding-your-companions',
  },
  {
    name: 'Medicine Tracker',
    description: 'Keep all your medications organized in one easy place, so you always know what to take, when to take it, and when it\'s time for a refill.',
    link: '/support/older-adults/medicine-tracker',
  },
  {
    name: 'Daily Wellness Log',
    description: 'Your personal health journal. Track hydration, mood, nutrition, pain, and sleep each day, so you can spot patterns and share helpful information with your doctor or family.',
    link: '/support/older-adults/daily-wellness-log',
  },
  {
    name: 'Vital Signs',
    description: 'Record key health measurements like blood pressure, heart rate, and weight, so you and your care team can monitor your health and spot changes over time.',
    link: '/support/older-adults/vital-signs',
  },
  {
    name: 'How I\'m Feeling',
    description: 'Your personal symptom journal. Record how you\'re feeling day to day, spot patterns over time, and share a clear picture of your health with your doctor, caregiver, or family.',
    link: '/support/older-adults/how-im-feeling',
  },
  {
    name: 'Health Info Finder',
    description: 'Get clear, trustworthy answers to your health questions, written in plain language with no confusing medical jargon, so you can feel confident and informed.',
    link: '/support/older-adults/health-info-finder',
  },
  {
    name: 'Home Safety Profile',
    description: 'A short questionnaire that personalizes your room checks. Answer a few questions about your home, health, and mobility so Home Safety Checks can give you recommendations that actually fit your life.',
    link: '/support/older-adults/home-safety-profile',
  },
  {
    name: 'Scan My Rooms',
    description: 'Your complete home safety checking tool. Photograph each room, receive AI-generated safety insights, and build a personalized action plan, all in about 5–10 minutes.',
    link: '/support/older-adults/scan-my-rooms',
  },
  {
    name: 'Room Scan History',
    description: 'Review past checks, track safety improvements over time, and revisit recommendations for each room. Turn any finding into a to-do with one tap.',
    link: '/support/older-adults/room-scan-history',
  },
  {
    name: 'Emergency Preparedness',
    description: 'Your critical medical details, emergency contacts, and care preferences, all in one place. Fill it out once, keep it current, and make sure the right people can access it when every second counts.',
    link: '/support/older-adults/emergency-info',
  },
  {
    name: 'My Documents',
    description: 'Your secure personal vault for storing and organizing important papers, wills, insurance, medical records, and more, so your family, caregivers, or doctors can find what they need quickly.',
    link: '/support/older-adults/my-documents',
  },
  {
    name: 'ThriveScore History',
    description: 'Your personal wellness scoreboard. See how your ThriveScore has grown over time, track your Home Safety and Activity & Wellness points, and celebrate your daily streaks.',
    link: '/support/older-adults/thrivescore-history',
  },
  {
    name: 'Fitness Videos',
    description: "Your personal exercise library, built for your body and your pace. Browse 20+ categories of expert-led videos, from Chair Yoga and Balance Training to Tai Chi and Calm Breathing, filter by what your body needs, save your favorites, set a monthly activity goal, and earn ThriveScore Wellness points every time you move.",
    link: '/support/older-adults/fitness-videos-getting-started',
  },
  {
    name: 'Brain Games',
    description: "Ten cognitive training games, all in one place. Play Pattern Flow, Mini Sudoku, Match & Go, Trivia Challenge, Word Ladder, and more, each designed to exercise a specific part of your brain. Games adapt to your level, sessions take 1–10 minutes, and every game you finish earns ThriveScore Wellness points.",
    link: '/support/older-adults/brain-games-getting-started',
  },
  {
    name: 'Log Activity',
    description: "Your personal activity journal. Record what you do each day across four wellness areas, physical movement, mind and memory, connecting with others, and daily routines. Every activity you log earns Thrive Points and builds your ThriveScore.",
    link: '/support/older-adults/log-activity-getting-started',
  },
];

const SupportOlderAdults = () => {
  const location = useLocation();
  const hashTopic = location.hash.replace('#', '');
  const [searchQuery, setSearchQuery] = useState('');
  const [openTopic, setOpenTopic] = useState(hashTopic || '');
  const { data: grouped, isLoading } = useSupabaseDocsGrouped('older-adults');
  const { data: searchResults } = useSupabaseDocsSearch('older-adults', searchQuery);

  // Scroll to and open accordion when hash changes or data loads
  useEffect(() => {
    if (hashTopic && grouped && grouped[hashTopic]) {
      setOpenTopic(hashTopic);
      setTimeout(() => {
        document.getElementById(`topic-${hashTopic}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [hashTopic, grouped]);

  const sortedTopics = useMemo(() => {
    if (!grouped) return [];
    return Object.entries(grouped)
      .sort(([, a], [, b]) => a.topic_order - b.topic_order)
      .map(([key, val]) => ({
        key,
        title: TOPIC_META[key]?.title || key,
        docs: val.docs,
      }));
  }, [grouped]);

  // Consolidate FAQ categories into 5 core buckets for older adults
  const FAQ_CATEGORY_MAP: Record<string, string> = {
    'getting-started': 'getting-started',
    'core-features': 'using-seniorthrive',
    'chat-companions': 'using-seniorthrive',
    'thrivecircle-family': 'family-sharing',
    'community-social': 'family-sharing',
    'plans-billing': 'account-billing',
    'technical-support': 'account-billing',
    'privacy-security': 'safety-privacy',
    'safety-emergency': 'safety-privacy',
    'professional-services': 'using-seniorthrive',
    'background-checks': 'safety-privacy',
  };

  const olderAdultFAQs: FAQ[] = useMemo(() => {
    return faqs
      .filter(faq => faq.audience.includes('older-adults'))
      .map(faq => ({
        ...faq,
        category: FAQ_CATEGORY_MAP[faq.category] || faq.category,
      }));
  }, []);
  const isSearching = searchQuery.length >= 2;

  return (
    <>
        <SEO
        title="Older Adult Support - SeniorThrive"
        description="Step-by-step guidance to help you thrive at home with confidence."
      />

      <div className="min-h-screen bg-background">
        {/* SECTION 1: Hero + Search */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Older Adult Support
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
              Step-by-step guidance to help you thrive at home with confidence.
            </p>
            <div className="relative max-w-xl mx-auto">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search support guides..."
                className="pl-10 h-12 text-base"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Search Results */}
            {isSearching && (
              <div className="mt-6 text-left max-w-xl mx-auto">
                {searchResults && searchResults.length > 0 ? (
                  <div className="space-y-3">
                    {searchResults.map((doc) => (
                      <Link
                        key={doc.id}
                        to={`/support/older-adults/${doc.slug}`}
                        className="block rounded-lg border p-4 hover:border-primary/40 hover:bg-muted/40 transition-colors"
                      >
                        <h3 className="font-bold text-foreground">{doc.title}</h3>
                        {doc.excerpt && (
                          <p className="text-sm text-muted-foreground mt-1">{doc.excerpt}</p>
                        )}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    No results found. Try a different search.
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* SECTION 2: Choose a Topic */}
        <section className="py-20 bg-muted/40">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-4">Choose a Topic</h2>
              <p className="text-muted-foreground text-lg">
                Pick what you're trying to do. We'll guide you step by step.
              </p>
            </div>

            {isLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full rounded-lg" />
                ))}
              </div>
            ) : (
              <Accordion type="single" collapsible value={openTopic} onValueChange={setOpenTopic} className="space-y-3">
                {sortedTopics.map((topic) => (
                  <AccordionItem key={topic.key} value={topic.key} id={`topic-${topic.key}`} className="border rounded-lg px-4">
                    <AccordionTrigger className="min-h-[44px] text-left text-base font-bold hover:no-underline">
                      {topic.title}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid gap-3 pt-2 pb-4">
                        {topic.docs.map((doc, idx) => (
                          <Link
                            key={doc.id}
                            to={`/support/older-adults/${doc.slug}`}
                            className="flex items-start gap-3 rounded-lg border p-4 hover:border-primary/40 hover:bg-muted/40 transition-colors"
                          >
                            {doc.journey_stage != null && (
                              <Badge variant="secondary" className="shrink-0 text-xs font-bold mt-0.5">
                                STEP {doc.journey_stage}
                              </Badge>
                            )}
                            {doc.journey_stage == null && (
                              <Badge variant="secondary" className="shrink-0 text-xs font-bold mt-0.5">
                                STEP {idx + 1}
                              </Badge>
                            )}
                            <div>
                              <h3 className="font-bold text-foreground">{doc.title}</h3>
                              {doc.excerpt && (
                                <p className="text-sm text-muted-foreground mt-1">{doc.excerpt}</p>
                              )}
                            </div>
                            <ArrowRightIcon className="h-4 w-4 ml-auto shrink-0 mt-1 text-muted-foreground" />
                          </Link>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </section>

        {/* SECTION 3: Understand the Tools */}
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">Understand the Tools</h2>
              <p className="text-muted-foreground text-lg">
                These are the tools mentioned in the guides above. Here's what each one does.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {TOOL_CARDS.map((tool) => (
                <Card key={tool.name} className="flex flex-col hover:shadow-md transition-shadow duration-200">
                  <CardContent className="flex flex-col flex-1 pt-6">
                    <h3 className="text-lg font-bold text-foreground mb-2">{tool.name}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1">{tool.description}</p>
                    <Link
                      to={tool.link}
                      className="inline-flex items-center text-sm font-medium text-primary hover:underline mt-4"
                    >
                      Learn more
                      <ArrowRightIcon className="h-3 w-3 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4: Frequently Asked Questions */}
        <section className="py-20 bg-muted/40">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
              <p className="text-muted-foreground text-lg">
                Quick answers to common questions about SeniorThrive.
              </p>
            </div>
            <FAQSection faqs={olderAdultFAQs} />
          </div>
        </section>

        {/* SECTION 5: All Guides (crawlable links for SEO discovery) */}
        {sortedTopics.length > 0 && (
          <section className="py-16">
            <div className="container mx-auto px-4 max-w-4xl">
              <h2 className="text-2xl font-bold text-foreground mb-6">All Support Guides</h2>
              <div className="columns-1 sm:columns-2 gap-x-8">
                {sortedTopics.map((topic) => (
                  <div key={topic.key} className="mb-6 break-inside-avoid">
                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wide mb-2">
                      {topic.title}
                    </h3>
                    <ul className="space-y-1">
                      {topic.docs.map((doc) => (
                        <li key={doc.id}>
                          <Link
                            to={`/support/older-adults/${doc.slug}`}
                            className="text-sm text-foreground hover:text-primary hover:underline transition-colors"
                          >
                            {doc.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* SECTION 6: Contact Support */}
        <ContactSection />
      </div>
    </>
  );
};

export default SupportOlderAdults;
